import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Tooltip from "@mui/material/Tooltip";

import ai from "../../../helpers/axios";
import OperatorRatingModal from "./operator-rating-modal";
import { RATING_STAGES } from "./verification-status";

const RATING_CRITERIA = [
  { key: "flightOverall", label: "Flight Overall" },
  { key: "communication", label: "Communication" },
  { key: "brokerFriendly", label: "Broker-friendly" },
  { key: "clientFeedback", label: "Client Feedback" },
];

const disruptionLines = (d) => {
  const m = d?.metadata || {};
  if (d.eventType === "signedOfrDataUpdated") {
    return m.editReason ? [{ text: m.editReason }] : [];
  }
  if (["cancelYesRefund", "cancelNoRefund"].includes(d.eventType)) {
    return [
      m.cancellationReason && { text: m.cancellationReason },
      m.cancellationReasonNotes && { text: m.cancellationReasonNotes, indent: true },
      m.notes && { text: m.notes, indent: true },
    ].filter(Boolean);
  }
  if (d.eventType === "addManualDisruption") {
    const parts = [m.disruptionCause, m.delays, m.aircraftChanges].filter(Boolean);
    return parts.length ? [{ text: parts.join(" | ") }] : [];
  }
  return [];
};

const buildOperators = (ofrs = []) => {
  const map = new Map();
  ofrs
    .filter((ofr) => RATING_STAGES.includes(ofr.stage))
    .forEach((ofr) => {
      const id = ofr.operator?.id;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, { id, name: ofr.operator?.name || "N/A", signed: 0, canceled: 0 });
      }
      const entry = map.get(id);
      if (ofr.stage === "signed") entry.signed += 1;
      if (ofr.stage === "canceled") entry.canceled += 1;
    });
  return [...map.values()].map((op) => ({ ...op, eligible: op.signed > 0 }));
};

const formatStageSummary = (op) => {
  const parts = [];
  if (op.signed > 0) parts.push(`Signed (${op.signed})`);
  if (op.canceled > 0) parts.push(`Cancelled (${op.canceled})`);
  return parts.join(", ");
};

const OperatorRatingsBlock = ({ tripId, trip, onChanged, readOnly }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [disruptions, setDisruptions] = useState([]);
  const [modal, setModal] = useState({ open: false, operator: null });

  const loadDisruptions = useCallback(async () => {
    if (!tripId) return;
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get(`/api/trips/${tripId}/disruptions`);
      setDisruptions(data?.data || []);
    } catch (error) {
      console.error("Error loading disruptions:", error);
      setDisruptions([]);
    }
  }, [tripId, getAccessTokenSilently]);

  useEffect(() => {
    loadDisruptions();
  }, [loadDisruptions]);

  const operators = useMemo(
    () => buildOperators(trip?.operatorFinancialRecords),
    [trip?.operatorFinancialRecords],
  );

  const ratingsByOperator = useMemo(() => {
    const map = {};
    (trip?.operatorRatings || []).forEach((r) => {
      map[r.operatorId] = r;
    });
    return map;
  }, [trip?.operatorRatings]);

  const disruptionsByOperator = useMemo(() => {
    const map = {};
    disruptions.forEach((d) => {
      const id = d.metadata?.operator?.id;
      if (!id) return;
      const lines = disruptionLines(d);
      if (!lines.length) return;
      (map[id] = map[id] || []).push(lines);
    });
    return map;
  }, [disruptions]);

  const incomplete = operators.some(
    (op) => op.eligible && !ratingsByOperator[op.id],
  );

  const openRate = (operator) => setModal({ open: true, operator });
  const closeModal = () => setModal({ open: false, operator: null });

  const openL2Disruptions = () =>
    window.open(`/trips/${tripId}?tab=disruptions`, "_blank", "noopener,noreferrer");

  const renderRating = (op) => {
    if (!op.eligible) return null;
    const rating = ratingsByOperator[op.id];
    if (!rating) {
      if (readOnly) return <span className="tv-text-danger">Not rated</span>;
      return (
        <button type="button" className="base-button btn-danger" onClick={() => openRate(op)}>
          Rate
        </button>
      );
    }
    return (
      <div className="tv-rating-wrapper">
        <div className="tv-rating-values">
          {RATING_CRITERIA.map(({ key, label }) => (
            <div key={key}>
              {label}: {rating[key] ?? "-"}
            </div>
          ))}
        </div>
        {!readOnly && (
          <button title="Edit rating" type="button" className="base-button evo-blue mt-1 icon-button icon-button-small"
                  onClick={() => openRate(op)}>
            <span className="material-icons">edit</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="tv-block tv-operator-ratings">
      <h5 className="tv-block-title">
        Operator Ratings
        {incomplete && <span className="tv-text-danger"> - Incomplete</span>}
      </h5>

      <div className="tv-block-body">
        {operators.length === 0 ? (
          <div className="tv-empty">No operators are available.</div>
        ) : (
          <table className="tv-table tv-ratings-table">
            <thead>
            <tr>
              <th>Operator</th>
              <th>Stage</th>
              <th>Rating</th>
            </tr>
            </thead>
            <tbody>
            {operators.map((op) => {
              const opDisruptions = disruptionsByOperator[op.id] || [];
              return (
                <>
                  <tr key={op.id}>
                    <td>
                      <Tooltip title={op.name || ""}>
                        <div className="tv-op-name ellipsis-cell">{op.name}</div>
                      </Tooltip>
                      {opDisruptions.length > 0 && (
                        <div>
                          Disruptions:

                          {!readOnly && (
                            <button
                              type="button"
                              className="base-button evo-blue icon-button icon-button-small mb-1"
                              onClick={openL2Disruptions}
                              title="Add disruptions"
                            >
                              <span className="material-icons">add_circle</span>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <div>{formatStageSummary(op)}</div>
                      {opDisruptions.length > 0 && (
                        <div>
                          {opDisruptions.map((lines, di) => (
                            <div key={di} className="tv-disruption-group d-inline-block w-100 overflow-hidden">
                              {lines.map((line, li) => (
                                <div
                                  key={li}
                                  className={line.indent ? "tv-disruption-indent ellipsis-cell" : "ellipsis-cell"}
                                >
                                  <Tooltip title={line.text}>{line.text}</Tooltip>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{renderRating(op)}</td>
                  </tr>
                </>
              );
            })}
            </tbody>
          </table>
        )}
      </div>

      <OperatorRatingModal
        isOpen={modal.open}
        onClose={closeModal}
        tripId={tripId}
        operator={modal.operator}
        existingRating={modal.operator ? ratingsByOperator[modal.operator.id] : null}
        onSaved={onChanged}
      />
    </div>
  );
};

export default OperatorRatingsBlock;
